import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Mail, Phone, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/utils/constants";
import { PhotoUpload } from "@/components/users/PhotoUpload";
import { formatPhoneNumber, handlePhoneChange, handlePhoneBlur } from "@/components/users/userUtils";

export const UserProfileModal = ({ open, onClose, currentUser, onUpdate }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  // Initialize form when modal opens
  React.useEffect(() => {
    if (open && currentUser) {
      setEditingUser({
        ...currentUser,
        first_name: currentUser.first_name || "",
        last_name: currentUser.last_name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
        photo: currentUser.photo || null,
        photo_crop: currentUser.photo_crop || null,
        newPhoto: null,
        newPhotoCrop: null
      });
    }
  }, [open, currentUser]);

  const handleSave = async () => {
    if (!editingUser.first_name || !editingUser.last_name || !editingUser.email || !editingUser.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        email: editingUser.email,
        phone: editingUser.phone
      };
      
      // Handle photo changes
      if (editingUser.photoDeleted) {
        updateData.photo = null;
        updateData.photo_crop = null;
      } else if (editingUser.newPhoto !== undefined && editingUser.newPhoto !== null) {
        updateData.photo = editingUser.newPhoto;
        updateData.photo_crop = editingUser.newPhotoCrop;
      }
      
      await axios.put(`${API}/users/${currentUser.id}`, updateData);
      toast.success("Profile updated successfully");
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (!editingUser) return null;

  const displayPhoto = editingUser.newPhoto !== undefined ? editingUser.newPhoto : editingUser.photo;
  const displayCrop = editingUser.newPhotoCrop !== undefined ? editingUser.newPhotoCrop : editingUser.photo_crop;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none max-h-[100dvh] m-0 rounded-none flex flex-col fixed inset-0 p-0 overflow-hidden sm:max-w-md sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:m-auto">
        <DialogHeader className="flex-shrink-0 p-4 border-b bg-white">
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-600" />
            Edit My Profile
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-lg mx-auto space-y-4">
            {/* Photo Upload */}
            <div className="flex justify-center pb-4 border-b border-slate-100">
              <PhotoUpload 
                photo={editingUser.newPhoto !== undefined ? editingUser.newPhoto : null}
                cropPosition={editingUser.newPhotoCrop !== undefined ? editingUser.newPhotoCrop : null}
                existingPhoto={editingUser.photo}
                existingCropPosition={editingUser.photo_crop}
                onPhotoChange={(photo, cropData) => {
                  if (photo === null && cropData === null) {
                    setEditingUser({ 
                      ...editingUser, 
                      newPhoto: null, 
                      newPhotoCrop: null,
                      photo: null,
                      photo_crop: null,
                      photoDeleted: true
                    });
                  } else {
                    setEditingUser({ 
                      ...editingUser, 
                      newPhoto: photo, 
                      newPhotoCrop: cropData,
                      photoDeleted: false 
                    });
                  }
                }}
              />
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="profile-first-name">First Name *</Label>
                <Input
                  id="profile-first-name"
                  value={editingUser.first_name}
                  onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                  placeholder="First Name"
                  className="h-11 mt-1"
                  data-testid="input-profile-first-name"
                />
              </div>
              <div>
                <Label htmlFor="profile-last-name">Last Name *</Label>
                <Input
                  id="profile-last-name"
                  value={editingUser.last_name}
                  onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                  placeholder="Last Name"
                  className="h-11 mt-1"
                  data-testid="input-profile-last-name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="profile-email">Email *</Label>
              <Input
                id="profile-email"
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                placeholder="your.email@company.com"
                className="h-11 mt-1"
                data-testid="input-profile-email"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="profile-phone">Phone Number *</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={formatPhoneNumber(editingUser.phone || "")}
                onChange={(e) => handlePhoneChange(e.target.value, setEditingUser)}
                onBlur={() => handlePhoneBlur(editingUser.phone, setEditingUser)}
                placeholder="(555) 123-4567"
                className="h-11 mt-1"
                data-testid="input-profile-phone"
              />
            </div>

            {/* Read-only fields */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-xs text-slate-500 font-medium">Account Information (Read-only)</p>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Position:</span>
                <span className="font-medium text-slate-700">{editingUser.position || "Not set"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Role:</span>
                <span className="font-medium text-slate-700 capitalize">{editingUser.role?.replace('_', ' ') || "Not set"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-t bg-white" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <Button variant="outline" onClick={onClose} className="h-10 px-4">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 h-10 px-4"
            data-testid="btn-save-profile"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
